import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { Canvas } from '@react-three/fiber'
import { ContactShadows, OrbitControls } from '@react-three/drei'
import { Env } from './environment'
import { applyMaterial } from './createMaterial'
import { loadTexturesFromJSON, unloadTextures } from './loadTexture'
import { loadModel } from './loadModel'
import { RendererSettings, RendererInfo } from './renderSettings'
import { CameraUpdater } from './cameraUpdater'
import CaptureComponent from './capture'
import { useHelper } from '@react-three/drei'
import { SpotLightHelper, DirectionalLightHelper, Color } from 'three'
import { findProductData, getMaterialTypes } from './utilities'

const isDevelopment =
  typeof window !== 'undefined' && window.location.hostname === 'localhost'

export function Viewer({
  productData: productDataArray,
  resolution = 'large',
  onViewerReady,
  onViewerError,
  environment = 'brown_photostudio_05_1k',
  className,
  containerClassName,
  onCapture,
  castShadow = false,
}) {
  const [error, setError] = useState(false)
  const [envError, setEnvError] = useState(false)
  const [envReady, setEnvReady] = useState(false)
  const [models, setModels] = useState([])
  const [materialsMap, setMaterialsMap] = useState([])
  const [texturesLoaded, setTexturesLoaded] = useState(false)
  const [modelsLoaded, setModelsLoaded] = useState(false)
  const [viewerReady, setViewerReady] = useState(false)
  const [texturesVersion, bumpTexturesVersion] = useState(0)

  const lastBumpedVersionRef = useRef(0)
  const inFlightVersionRef = useRef(0)
  const modelsCount = useRef(0)

  const [sceneMounted, setSceneMounted] = useState(false)
  const mountedUUIDsRef = useRef(new Set()) // refs van de modellen bijhouden die gemounted zijn in de scene

  // map voor de materials die in actieve selectie zijn (modelCode:type)
  function buildSelectionMap(productData) {
    const map = new Map()
    for (const p of productData) {
      for (const m of p.materialData || []) {
        map.set(`${p.modelCode}:${m.type}`, {
          code: m.code,
          material: m.material,
        })
      }
      for (const cs of p.crossSellData || []) {
        for (const m of cs.materialData || []) {
          map.set(`${cs.modelCode}:${m.type}`, {
            code: m.code,
            material: m.material,
          })
        }
      }
    }
    return map
  }

  // de laatste selectie tracken, zodat we een diff kunnen doen
  const prevSelectionRef = useRef(new Map())

  // enkel de veranderde paren
  const [changeSet, setChangeSet] = useState({
    keys: new Set(),
    materials: new Set(),
    version: 0,
  })

  // priming van de viewer sticky maken
  const [texturesBootstrapped, setTexturesBootstrapped] = useState(false)

  const controlsRef = useRef()
  const canvasRef = useRef()

  const modelsStructure = useMemo(() => {
    return models
      .map(
        ({ model }) =>
          model?.children?.map((mesh) => mesh.name).join(',') || '',
      )
      .join(',')
  }, [models])

  // wanneer de modellen structuur of het modellen aantal wijzigt, reset de mount tracker
  useEffect(() => {
    // console.log('sceneMounted resetten')
    mountedUUIDsRef.current.clear()
    setSceneMounted(false)
  }, [modelsStructure])

  // environment kan niet ingeladen worden
  const handleEnvError = useCallback(
    (error) => {
      // console.log('handleEnvError called with error:', error)
      if (!envError && onViewerError) {
        setEnvError(true)
        setError(error)
        onViewerError(`😣 Failed to load environment map: ${error}`)
      }
    },
    [envError, onViewerError],
  )

  // environment is klaar
  const handleEnvReady = useCallback(
    (ready) => {
      if (ready && !envError && !envReady) {
        console.log('✋ Environment ready.')
        setEnvReady(() => ready)
      }
    },
    [envError, envReady],
  )

  // const materialTypes = useMemo(
  //   () => getMaterialTypes(productDataArray),
  //   [productDataArray],
  // )

  // welke modellen hebben we nodig volgens de actieve productData
  useEffect(() => {
    const requiredCodes = new Set(
      productDataArray.flatMap((p) => [
        p.modelCode,
        ...(p.crossSellData || []).map((x) => x.modelCode),
      ]),
    )

    if (requiredCodes.size && modelsCount.current !== requiredCodes.size) {
      modelsCount.current = requiredCodes.size
    }
  }, [models, productDataArray])

  // material diffs
  // useEffect(() => {
  //   const next = buildSelectionMap(productDataArray)
  //   const changedKeys = new Set()
  //   const changedMaterials = new Set()
  //
  //   next.forEach((val, key) => {
  //     const prev = prevSelectionRef.current.get(key)
  //     if (!prev || prev.code !== val.code) {
  //       changedKeys.add(key)
  //       changedMaterials.add(val.material)
  //     }
  //   })
  //
  //   if (changedKeys.size > 0) {
  //     setChangeSet((prev) => ({
  //       keys: changedKeys,
  //       materials: changedMaterials,
  //       version: prev.version + 1,
  //     }))
  //   }
  //
  //   prevSelectionRef.current = next
  // }, [productDataArray])

  // bijhouden of de sockets al geset zijn voor de huidige materialen
  // const socketsAttachKeyRef = useRef('')

  // socket attachment
  // useEffect(() => {
  //   // console.log(
  //   //   '- In socket application: ',
  //   //   envReady,
  //   //   modelsLoaded,
  //   //   sceneMounted,
  //   // )
  //
  //   if (envError) return
  //   // if (!envReady) return
  //   if (!modelsLoaded) return // modellen zijn geladen
  //   if (!sceneMounted) return // en modellen zijn gemount aan de scene, dus staan op z'n plaats, klaar om sockets te vullen
  //   if (productDataArray.length === 0) return
  //
  //   // actieve xsells met een key bijhouden en daarop vergelijken
  //   const activeXsells = new Set(
  //     productDataArray.flatMap((p) =>
  //       (p.crossSellData || []).map((x) => x.modelCode),
  //     ),
  //   )
  //   const attachKey = modelsStructure + '|' + [...activeXsells].sort().join(',')
  //
  //   // niks veranderd, dus bailen
  //   if (socketsAttachKeyRef.current === attachKey) return
  //
  //   console.log('⚙️ Apply sockets (structure changed)')
  //
  //   // sockets setten
  //   productDataArray.forEach((product) => {
  //     // const meshes = models.flatMap(({ model }) => model?.children || [])
  //     //
  //     // console.log('all children: ', meshes)
  //
  //     // product.crossSellData.forEach((crossSellItem) => {
  //     //   // hard match first
  //     //   let relevantSockets = sockets.filter(
  //     //     (socket) =>
  //     //       (socket.name.startsWith(`socket_${crossSellItem.modelCode}`) &&
  //     //         socket.name.endsWith(`socket_${crossSellItem.modelCode}`)) ||
  //     //       socket.name.startsWith(`socket_${crossSellItem.modelCode}0`) ||
  //     //       socket.name.startsWith(`socket_${crossSellItem.modelCode}_`),
  //     //   )
  //     //
  //     //   if (!relevantSockets.length) {
  //     //     // fuzzy match wanneer geen matches
  //     //     relevantSockets = sockets.filter((socket) =>
  //     //       socket.name.startsWith(`socket_${crossSellItem.modelCode}`),
  //     //     )
  //     //   }
  //     //
  //     //   if (!relevantSockets.length) return
  //     //
  //     //   relevantSockets.sort((a, b) => a.name.length - b.name.length)
  //     //
  //     //   const xsell = models.find(
  //     //     (m) => m.product.modelCode === crossSellItem.modelCode,
  //     //   )
  //     //   if (!xsell) return
  //     //
  //     //   for (let i = 0; i < relevantSockets.length; i++) {
  //     //     const socket = relevantSockets[i]
  //     //
  //     //     // is er al 1, loop dan door
  //     //     const exists = socket.children.find((child) =>
  //     //       child.name?.startsWith(xsell.product.modelCode),
  //     //     )
  //     //     if (exists) continue
  //     //
  //     //     if (i === 0) {
  //     //       // console.log(
  //     //       //   `filling socket: ${socket.name} with ${xsell.product.modelCode}`,
  //     //       // )
  //     //       // originele 1x
  //     //       socket.add(xsell.model)
  //     //       xsell.model.matrix.identity()
  //     //       xsell.model.updateMatrixWorld(true)
  //     //     } else {
  //     //       // klonen voor bijkomende sockets
  //     //       // console.log(
  //     //       //   `filling cloned socket: ${socket.name} with ${xsell.product.modelCode}`,
  //     //       // )
  //     //       const clone = xsell.model.clone(true) // deep clone
  //     //       clone.name = xsell.model.name // naam bijhouden zodat existingChild werkt
  //     //       socket.add(clone)
  //     //       clone.matrix.identity()
  //     //       clone.updateMatrixWorld(true)
  //     //     }
  //     //   }
  //     // })
  //   })
  //
  //   // structuur is toegepast, referentie setten
  //   socketsAttachKeyRef.current = attachKey
  // }, [
  //   envReady,
  //   envError,
  //   modelsLoaded,
  //   modelsStructure,
  //   productDataArray,
  //   sceneMounted,
  // ])

  // een soort ready functie, maar zonder camera update, daarop wordt nog eens gewacht
  const isPrimed = useMemo(() => {
    return (
      envReady &&
      !envError &&
      texturesBootstrapped &&
      modelsLoaded &&
      productDataArray.length > 0
    )
  }, [
    envReady,
    envError,
    texturesBootstrapped,
    modelsLoaded,
    productDataArray.length,
  ])

  // textures laadfunctie
  useEffect(() => {
    console.log('- In textures load functie: ', productDataArray)
    if (!envReady || envError) return
    if (!modelsLoaded) return
    if (texturesBootstrapped) return
    if (texturesLoaded) return
    if (!productDataArray.length) return

    console.log(productDataArray)

    let modelName = productDataArray[0].modelData.fields['Commercial Item']

    // ext
    let ext_color = productDataArray[0].modelData.fields[
      'Exterior Color'
    ].replace(' ', '')

    let ext_color_suffix = ''

    // mid (int)
    let int_color = productDataArray[0].modelData.fields['Int. Color'].replace(
      ' ',
      '',
    )

    // mid embossing
    if (
      Object.keys(productDataArray[0].modelData.fields).includes(
        'Int. Embossing',
      )
    ) {
      int_color +=
        productDataArray[0].modelData.fields['Int. Embossing'].toLowerCase()
    }

    if (modelName.endsWith('PG') || modelName.endsWith('PGT')) {
      ext_color_suffix = 'PG'
    }

    if (modelName.endsWith('DM')) {
      ext_color_suffix = 'DM'
    }

    if (!int_color.startsWith('RAL')) {
      int_color = 'RAL' + int_color
    }

    ext_color += ext_color_suffix

    console.log('modelName:', modelName)
    console.log('ext color:', ext_color)
    console.log('int color:', int_color)

    let int_has_stucco = int_color.indexOf('stucco') !== -1

    let materials = [
      {
        code: 'int',
        color_identifier: int_color,
        transparent: false,
        sheen: 0,
        sheenRoughness: 0,
        sheenColor: '#FFFFFF',
        metalness: 1,
        thickness: 0,
        transmission: 0,
        color: int_has_stucco ? '#F1ECE1' : '#E7DB90',
        clearcoat: 0,
        clearcoatRoughness: 0,
        normalScale: 1,
        roughness: 1,
        mapTypes: {
          baseColor: !int_has_stucco,
          normal: `${int_color}_normal`,
          roughness: `${int_color}_roughness`,
          opacity: false,
          // displacement: `${int_color}_displacement`,
          metalness: !int_has_stucco,
        },
      },
      {
        code: 'mid',
        transparent: false,
        sheen: 0,
        sheenRoughness: 0,
        sheenColor: '#FFFFFF',
        metalness: 1,
        thickness: 0,
        transmission: 0,
        color: '#E7DB90',
        clearcoat: 0,
        clearcoatRoughness: 0,
        normalScale: 1,
        roughness: 1,
        mapTypes: {
          baseColor: false,
          normal: `MID_normal`,
          roughness: false,
          opacity: false,
          // displacement: false,
          metalness: false,
        },
      },
      {
        code: 'ext',
        color_identifier: ext_color,
        transparent: false,
        sheen: 0,
        sheenRoughness: 0,
        sheenColor: '#FFFFFF',
        metalness: 1,
        thickness: 0,
        transmission: 0,
        color: '#0E0E10',
        clearcoat: 0,
        clearcoatRoughness: 0.1,
        normalScale: 1,
        roughness: 0.01,
        mapTypes: {
          baseColor: `${ext_color}_basecolor`,
          normal: `${ext_color_suffix}_normal`,
          roughness: `${ext_color}_roughness`,
          opacity: false,
          // displacement: `${ext_color_suffix}_displacement`,
          metalness: `${ext_color}_metalness`,
        },
      },
    ]

    if (!texturesBootstrapped) {
      setTexturesBootstrapped(true) // enkel voor eerste bootstrap
      setMaterialsMap([])

      // check
      let toLoad = []

      materials.forEach((material) => {
        if (
          materialsMap.findIndex(
            (element) => material.code === element.code,
          ) === -1
        ) {
          toLoad.push(material)
        }
      })

      if (toLoad.length) {
        Promise.all(
          toLoad.map((item) => {
            console.log(`🟡 Requesting textures for: ${item.code}`)

            return loadTexturesFromJSON(item, resolution, (err) => {
              if (err)
                console.error(`🔴 Error while requesting ${item.code}:`, err)
            })
              .then((textures) => {
                item.textures = textures
                item.loaded = true
                materialsMap.push(item)
                setMaterialsMap(materialsMap)
                console.log(`🟢 Loaded textures for: ${item.code}`)
              })
              .catch((err) =>
                console.error(
                  `🔴 Failed to load textures for: ${item.code}`,
                  err,
                ),
              )
          }),
        )
          .then(() => {
            setTexturesLoaded(true)
            setTexturesBootstrapped(true)
          })
          .catch((error) => {
            onViewerError && onViewerError(error)
          })
      }
    }
  }, [
    // changeSet.version,
    // changeSet.materials,
    envReady,
    envError,
    modelsLoaded,
    materialsMap,
    resolution,
    onViewerError,
    productDataArray,
    texturesBootstrapped,
  ])

  // materials toepassen als texturen/modellen veranderen
  useEffect(() => {
    if (!sceneMounted) return
    if (!texturesLoaded) return

    const meshes = models.flatMap(({ model }) => model?.children || [])
    console.log('meshes:', meshes)

    models.forEach(({ model, product }) => {
      model?.traverse((child) => {
        if (!child.isMesh) return

        materialsMap.forEach((material) => {
          if (child?.name.endsWith(material.code)) {
            console.log(
              `🖼️ Applying material ${material.code} on model ${model.name} - ${child.name}`,
            )
            applyMaterial(
              model,
              child.material,
              material,
              material.textures,
              (error) => {
                setError(error)
                onViewerError && onViewerError(error)
              },
            )
          }
        })
      })
    })

    if (!viewerReady) {
      setViewerReady(true)
      onViewerReady?.(true) // belangrijk in zowel front als Puppeteer dat viewer hierna pas zichtbaar is
    }
  }, [models, sceneMounted, texturesLoaded, envReady, materialsMap])

  // xsell visibiliteit, runt als productData wordt aangepast of modellen veranderen
  // useEffect(() => {
  //   if (!models.length) return
  //   if (!modelsLoaded) return
  //   if (!texturesLoaded) return
  //   if (!productDataArray.length) return
  //
  //   const mainCode = productDataArray[0]?.modelCode
  //   if (!mainCode) return
  //
  //   // actieve xsell codes voor de huidige selectie
  //   const activeXsells = new Set(
  //     (productDataArray[0]?.crossSellData || []).map((x) => x.modelCode),
  //   )
  //
  //   // console.log(
  //   //   '- hidden xsells productdata: ',
  //   //   productDataArray[0],
  //   //   activeXsells,
  //   // )
  //
  //   // visibility checken
  //   models.forEach(({ model }) => {
  //     model?.traverse((child) => {
  //       if (!child.isMesh) return
  //
  //       // alle meshes altijd standaard zichtbaar maken
  //       child.visible = true
  //
  //       // ^[A-Z0-9]+[_a-z]+ matched met "PILBRH6040P_colour_cushion", dan en enkel dan een check doen
  //       const xsellCodeRegEx = /^[A-Z0-9]+_[a-z]/
  //
  //       // console.log('mesh: ' + child.name)
  //
  //       // vind je een mesh waarbij de naam een configureerbare notatie heeft, check dan of die begint met de mainCode of niet in de xsells zit
  //       if (
  //         !child.name.startsWith(mainCode) &&
  //         xsellCodeRegEx.test(child.name)
  //       ) {
  //         // console.log('mesh in check: ' + child.name)
  //
  //         child.visible = [...activeXsells].some((code) =>
  //           child.name.split('_').includes(code),
  //         )
  //
  //         xsellCodeRegEx.lastIndex = 0
  //       }
  //     })
  //   })
  // }, [models, modelsLoaded, texturesLoaded, productDataArray])

  // modellen laden, incl cancellation support
  useEffect(() => {
    if (!envReady || envError) return
    let cancelled = false

    const loadProductModels = async (products) => {
      await Promise.all(
        products.map(async (product) => {
          const existingModel = models.find(
            (m) => m.product.modelCode === product.modelCode,
          )

          if (!existingModel) {
            try {
              await new Promise((resolve, reject) => {
                loadModel(
                  product.modelCode,
                  (loadedModel) => {
                    if (cancelled) return
                    loadedModel.name = product.modelCode
                    console.log(`🆕 Start loading model: ${product.modelCode}`)
                  },
                  (error) => {
                    if (cancelled) return
                    console.error(
                      `😣 Error loading model ${product.modelCode}:`,
                      error,
                    )
                    onViewerError && onViewerError(error)
                    reject(error)
                  },
                  (loadedModel) => {
                    if (cancelled) return
                    console.log(`✅ Done loading model ${product.modelCode}`)
                    setModels((prevModels) => [
                      ...prevModels,
                      { model: loadedModel, product, ready: true },
                    ])
                    resolve()
                  },
                )
              })
            } catch (error) {
              if (!cancelled) {
                console.error(
                  `😣 Error loading model ${product.modelCode}:`,
                  error,
                )
                onViewerError && onViewerError(error)
              }
            }
          }
        }),
      )
    }

    if (productDataArray && productDataArray.length > 0) {
      const list = productDataArray.flatMap((product) => [
        product,
        ...(product.crossSellData || []),
      ])
      loadProductModels(list)
    }

    return () => {
      cancelled = true
    }
  }, [productDataArray, onViewerError, models, envReady, envError])

  function Lights() {
    const light = useRef()

    // useHelper(light, DirectionalLightHelper, 2, 'green')
    //
    // const bulb = useMemo(() => new Color('#ffd54a'), [])
    //
    // return (
    //   <>
    //     <directionalLight
    //       ref={light}
    //       castShadow
    //       position={[-5, 10, 5]}
    //       intensity={0.5}
    //       shadow-mapSize={[1024, 1024]}
    //     >
    //       <orthographicCamera
    //         attach="shadow-camera"
    //         args={[-6, 6, 6, -6, 1, 20]}
    //       />
    //     </directionalLight>
    //
    //     {/*bulb marker*/}
    //     <mesh position={[-5, 0, -5]}>
    //       <sphereGeometry args={[0.15, 16, 16]} />
    //       <meshBasicMaterial color={bulb} />
    //     </mesh>
    //   </>
    // )

    //useHelper(light, SpotLightHelper, 2, 'green')

    return (
      <>
        <spotLight
          position={[0, 6.0, 0]}
          angle={Math.PI * 0.6}
          penumbra={0.9}
          intensity={1}
          distance={10}
          decay={2}
          castShadow
          shadow-mapSize={[1024, 1024]}
          shadow-bias={-0.0005}
          shadow-normalBias={0.02}
        ></spotLight>
      </>
    )
  }

  return (
    <div className={containerClassName}>
      <Canvas
        shadows
        gl={{ alpha: true }}
        ref={canvasRef}
        className={className}
        camera={{ fov: 30 }}
        style={{
          opacity: viewerReady ? 1 : 0,
        }}
      >
        <Lights></Lights>

        <>
          {models.map(({ model }, index) =>
            model ? (
              // deze models map runt telkens de models var is aangepast, dus telkens 1 model ingeladen wordt, loopt deze map opnieuw, niet ideaal maar het zij zo.
              <primitive
                key={index}
                object={model}
                position={[0, 0.135, 0]}
                // R3F attached het object in de scene
                ref={(obj) => {
                  if (!obj) return // wordt ook gecalled voor unmounts, dat negeren
                  const set = mountedUUIDsRef.current
                  if (!set.has(obj.uuid)) {
                    set.add(obj.uuid)
                    if (set.size === models.length) {
                      setSceneMounted(true) // alle <primitive>s zitten in de scene en zijn geattacheerd, sockets kunnen geset worden
                      if (modelsCount.current === set.size) {
                        setModelsLoaded(true)
                        console.log(
                          '🥳 All models are mounted, ready for socket attachment',
                        )
                      }
                    }
                  }
                }}
              />
            ) : null,
          )}
        </>

        <Env
          background={false}
          map={environment}
          rotation={-2}
          onDone={handleEnvReady}
          onError={handleEnvError}
        />

        <OrbitControls
          ref={controlsRef}
          enabled={viewerReady}
          minPolarAngle={-Math.PI / 0.5}
          maxPolarAngle={Math.PI / 2.2}
          enablePan={false}
          maxDistance={10}
          minDistance={1}
          autoRotate={false}
        />

        <CameraUpdater
          models={models}
          modelsStructure={modelsStructure}
          controlsRef={controlsRef}
          viewerPrimed={isPrimed} // trigger CameraUpdater fit/update
        />
        <CaptureComponent
          canvas={canvasRef}
          onCapture={onCapture}
          viewerReady={viewerReady}
        />

        <RendererSettings toneMappingExposure={2} shadowMap />

        {modelsLoaded && castShadow && (
          <mesh receiveShadow rotation-x={-Math.PI / 2} position={[0, 0, 0]}>
            <planeGeometry args={[7, 7]}></planeGeometry>
            <shadowMaterial opacity={0.06} />
          </mesh>
        )}

        {isDevelopment && <RendererInfo materialsMap={materialsMap} />}
      </Canvas>
    </div>
  )
}
